import { addDoc, collection, doc, getDoc, getDocs, query, setDoc, updateDoc, where, getAggregateFromServer, sum } from "firebase/firestore"
import { db, storage } from "./config"
import { getSellerIdsForUser } from "./shopify"
import { getDocsWhereIn } from "./queries"
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { getIncomeHoldDays } from "../utils/fees"

const generateUsername = (email) => {
    const username = email.split('@')[0];
    return username;
};

const getUserCountByUsernamePrefix = async (prefix) => {
    const usersRef = collection(db, 'users');

    // Create a query to filter usernames that start with the specified prefix
    const q = query(
        usersRef,
        where('username', '>=', prefix),
        where('username', '<', prefix + '\uf8ff') // This ensures it captures all usernames starting with the prefix
    );

    try {
        const querySnapshot = await getDocs(q);
        const userCount = querySnapshot.size; // Get the number of documents
        console.log(`Number of users with usernames starting with '${prefix}':`, userCount);
        return userCount;
    } catch (error) {
        console.error("Error getting users:", error);
        return 0; // Return 0 or handle error as needed
    }
};
export const createVisit = async ({ user }) => {
    try {
        const visitsRef = collection(db, "visits");

        // Get the start and end of today
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0); // Set to the start of the day
        const endOfToday = new Date();
        endOfToday.setHours(23, 59, 59, 999); // Set to the end of the day

        // Query to check if a visit with the given user_id exists and created_at is today
        const q = query(
            visitsRef,
            where("user_id", "==", user.id),
            where("created_at", ">=", startOfToday),
            where("created_at", "<=", endOfToday)
        );

        const querySnapshot = await getDocs(q);

        // If no documents exist with the provided user_id and created_at is today, create a new visit
        if (querySnapshot.empty) {
            // Add a new document with a generated ID
            const docRef = await addDoc(visitsRef, {
                user_id: user.id, // Ensure to include user_id
                membership: user.membership || "free", // Include membership if available
                created_at: new Date(),
            });
            console.log("Document written with ID: ", docRef.id);
        } else {
            console.log("Visit already exists for user_id:", user.id, "today.");
        }
    } catch (e) {
        console.error("Error adding visit: ", e);
    }
}

export async function createUserProfile(userId, email) {
    try {
        const existing = await checkUserExists(userId)

        if (!existing) {
            const username = generateUsername(email)
            await setDoc(doc(db, "users", userId), {
                username: username,
                role: 'customer',
                gender: 'man',
                displayName: username,
                picture: "https://firebasestorage.googleapis.com/v0/b/card-market-place-a2684.firebasestorage.app/o/images%2Fusers%2Fdefault.png?alt=media&token=3da906c9-a825-4f0b-b47c-ab6d10eefe89",
                balance: 0,
                cartList: [],
                membership: 'free',
                monthly_sales: 0,
                email: email,
                created_at: new Date(),
                ratings: 0
            });
        }

        console.log("User profile created successfully");
    } catch (error) {
        console.error("Error creating user profile: ", error);
    }
}

export const updateUserProfile = async (userId, updatedData) => {
    try {
        // Reference to the specific user document
        let pictureURL

        const usersRef = doc(db, "users", userId)
        const docSnap = await getDoc(usersRef)
        const querySnapshot = docSnap.data()
        console.log(updatedData);
        if (updatedData.picture !== null && updatedData.picture !== undefined) {
            const storageRef = ref(storage, `images/users/${querySnapshot.username}`)
            await uploadBytes(storageRef, updatedData.picture)
            pictureURL = await getDownloadURL(storageRef)
        }

        // Add a new document with a generated ID
        const newPictureURL = updatedData.picture ? pictureURL : querySnapshot.picture
        const newData = {
            ...updatedData,
            ...{
                picture: newPictureURL
            }
        }

        // Update the usersRef with new data
        await updateDoc(usersRef, newData)

        console.log("Document updated with ID: ", userId)
    } catch (e) {
        console.error("Error updating document: ", e)
    }
}

// export const checkUserExists = async (username) => {
//     try {
//         const usersRef = collection(db, "users")
//         const q = query(usersRef, where("username", "==", username))
//         const querySnapshot = await getDocs(q)

//         if (!querySnapshot.empty) {
//             return true // User exists
//         } else {
//             return false // User does not exist
//         }
//     } catch (e) {
//         console.error("Error checking user:", e)
//     }
// }

export const checkUserExists = async (userId) => {
    try {
        const userRef = doc(db, "users", userId);
        const userSnapshot = await getDoc(userRef);

        if (userSnapshot.exists()) {
            return true; // User exists
        } else {
            console.log("User does not exist");
            return false; // User does not exist
        }
    } catch (e) {
        console.error("Error checking user:", e)
    }
}

export const getUserData = async (userId) => { 
    try {
        console.log('getUserData - Fetching data for userId:', userId); // Debug log
        
        // Reference to the specific user document
        const usersRef = doc(db, "users", userId)
        const userSnapshot = await getDoc(usersRef)
        const userData = userSnapshot.data()
        
        console.log('getUserData - Raw user data from Firestore:', userData); // Debug log
        console.log('getUserData - Phone number from Firestore:', userData?.phoneNumber); // Debug log

        const messagesRef = collection(db, "messages")
        const unReadMessages = []
        const q = query(messagesRef,
            where("recipientId", "==", userId),
            where("state", "==", 'unread'))
        const querySnapshot = await getDocs(q)

        const purchases = userData.purchases ? userData.purchases : 0

        querySnapshot.forEach((doc) => {
            unReadMessages.push({ id: doc.id, ...doc.data() })
        })

        // What is held back from the withdrawable balance. This mirrors the
        // server's getWithdrawalHold(), which is what actually gates a
        // withdrawal -- the figure shown here has to agree with it.
        //
        // Two things were wrong before. It queried `sender`, which is the
        // BUYER on a sale, so it summed the income other sellers earned from
        // this user's purchases: a pure seller had nothing held, and a heavy
        // buyer saw their own balance docked money they never earned. And it
        // filtered on `created_at` when income is credited at `delivered_at`,
        // so any sale that took longer than the window to be confirmed was
        // released with no hold at all.
        //
        // Every plan holds something; the faster ones just hold it for fewer
        // days -- 3 on Pro and Elite, 7 on Basic.
        const holdDays = getIncomeHoldDays(userData?.membership);
        const holdSince = new Date(Date.now() - holdDays * 24 * 60 * 60 * 1000);

        // A Shopify sale is received by the store's role id, not the
        // owner's uid, so a store owner's income arrives under that id --
        // the same ids the server sums over. Missing them here would
        // under-report the hold and the withdrawal would then be refused
        // for a balance the UI had called available.
        const sellerIds = await getSellerIdsForUser(userId);

        const held = await Promise.all(sellerIds.map(async (sellerId) => {
            const base = [
                where("receiver", "==", sellerId),
                where("status", "==", "delivered")
            ];

            try {
                const salesSnapshot = await getAggregateFromServer(
                    query(collection(db, "sales"), ...base, where("delivered_at", ">", holdSince)),
                    { totalAmount: sum("sellerIncome") }
                );
                return salesSnapshot.data().totalAmount || 0;
            } catch (e) {
                // Almost certainly the (receiver, status, delivered_at)
                // composite index is missing. Letting this throw would
                // reject the whole getUserData() call and the profile
                // would load as null, so fall back the same way the
                // server does: equality filters only, which need no
                // composite index, and apply the date cutoff here.
                console.warn("Recent-income aggregate unavailable, falling back to a scan:", e?.message)
                const snapshot = await getDocs(query(collection(db, "sales"), ...base))
                return snapshot.docs.reduce((total, saleDoc) => {
                    const data = saleDoc.data()
                    const deliveredAt = data.delivered_at?.toDate
                        ? data.delivered_at.toDate()
                        : data.delivered_at && new Date(data.delivered_at)
                    return deliveredAt && deliveredAt > holdSince
                        ? total + (Number(data.sellerIncome) || 0)
                        : total
                }, 0)
            }
        }));

        const pendingBalance = held.reduce((total, amount) => total + amount, 0);
        
        
        const ratings = await getUserRating(userId)
        
        const result = { ...userData, id: userId, ratings, unReadMessages: unReadMessages, purchases, pendingBalance };
        console.log('getUserData - Final result:', result); // Debug log
        console.log('getUserData - Phone number in result:', result?.phoneNumber); // Debug log
        
        return result;
    } catch (e) {
        console.error("Error getting document: ", e)
        return null; // Return null instead of undefined
    }
}

export const getUserRating = async (userId) => {
    try {
        // Reference to the "sales" collection
        const salesRef = collection(db, "sales")

        // Includes sales made through any Shopify store this account owns --
        // those carry the store's role id as receiver, not the owner's uid.
        const docs = await getDocsWhereIn(salesRef, "receiver", await getSellerIdsForUser(userId))

        if (docs.length === 0) return 0


        let ratings = 0, count = 0

        docs.forEach((doc) => {
            if (doc.data().review) {
                ratings += doc.data().review.rating
                count++
            }
        })

        if (ratings > 0)
            return parseFloat(ratings / count) // Return the array of sales

        else return 0
    } catch (e) {
        console.error("Error getting documents: ", e)
        return [] // Return an empty array in case of error
    }
}

export const getUserReviews = async (userId) => {
    try {
        // Reference to the "sales" collection
        const salesRef = collection(db, "sales")

        // Includes sales made through any Shopify store this account owns --
        // those carry the store's role id as receiver, not the owner's uid.
        const docs = await getDocsWhereIn(salesRef, "receiver", await getSellerIdsForUser(userId))
        const reviews = []
        let countsByRating = [0, 0, 0, 0, 0]

        docs.forEach((doc) => {
            if (doc.data().review) {
                reviews.push(doc.data().review)
                countsByRating[doc.data().review.rating - 1]++
            }
        })

        return { reviews, countsByRating } // Return the array of sales
    } catch (e) {
        console.error("Error getting documents: ", e)
        return [] // Return an empty array in case of error
    }
}

export const getAllUserData = async () => {
    try {
        // Reference to the users collection
        const usersRef = collection(db, "users");
        const userSnapshot = await getDocs(usersRef);

        // Map through the documents and extract data
        const usersList = userSnapshot.docs.map(doc => ({
            id: doc.id,
            tickets: 0,
            ...doc.data()
        }));

        return usersList;
    } catch (e) {
        console.error("Error getting documents: ", e);
    }
}

export const addRecipient = async (userId, recipientId) => {
    try {
        // Reference to the specific user document
        const usersRef = doc(db, "users", userId)
        const userSnapshot = await getDoc(usersRef)
        const userData = userSnapshot.data()
        let recipients = userData.recipients ? userData.recipients : []
        const getRecipientData = await getUserData(recipientId)

        if (recipients.findIndex(item => item.id === recipientId) === -1) {
            recipients.push({ id: recipientId, displayName: getRecipientData.displayName, picture: getRecipientData.picture })
        }

        await updateDoc(usersRef, { ...userData, recipients: recipients })
    } catch (e) {
        console.error("Error getting document: ", e)
    }
}

export const checkPhoneNumberExists = async (phoneNumber, currentUserId) => {
    try {
        console.log('Checking phone number:', phoneNumber, 'for user:', currentUserId);
        const usersRef = collection(db, "users");
        const q = query(usersRef, where("phoneNumber", "==", phoneNumber));
        const querySnapshot = await getDocs(q);

        console.log('Query result size:', querySnapshot.size);

        if (querySnapshot.empty) {
            console.log('Phone number not found in database');
            return { exists: false, userId: null };
        }

        // Check if the phone number belongs to the current user
        const existingUser = querySnapshot.docs[0];
        console.log('Found user with phone number:', existingUser.id, 'Current user:', currentUserId);
        
        if (existingUser.id === currentUserId) {
            console.log('Phone number belongs to current user, allowing update');
            return { exists: false, userId: null }; // Same user, allow update
        }

        console.log('Phone number belongs to different user, blocking');
        return { exists: true, userId: existingUser.id };
    } catch (error) {
        console.error("Error checking phone number:", error);
        return { exists: false, userId: null };
    }
}

export const saveShippingInfo = async (userId, shippingInfo) => {
    try {
        const userRef = doc(db, "users", userId);
        await updateDoc(userRef, {
            shippingInfo: shippingInfo,
            updatedAt: new Date()
        });
        console.log('Shipping information saved successfully');
        return true;
    } catch (error) {
        console.error('Error saving shipping information:', error);
        throw error;
    }
};

export const getShippingInfo = async (userId) => {
    try {
        const userRef = doc(db, "users", userId);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
            const userData = userDoc.data();
            return userData.shippingInfo || null;
        }
        return null;
    } catch (error) {
        console.error('Error getting shipping information:', error);
        return null;
    }
};

export const updateShippingInfo = async (userId, updatedShippingInfo) => {
    try {
        const userRef = doc(db, "users", userId);
        await updateDoc(userRef, {
            shippingInfo: updatedShippingInfo,
            updatedAt: new Date()
        });
        console.log('Shipping information updated successfully');
        return true;
    } catch (error) {
        console.error('Error updating shipping information:', error);
        throw error;
    }
};